import {
	createContext,
	type JSX,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

export interface EditSessionSection {
	readonly canSave: boolean;
	readonly dirty: boolean;
	/** Translated validation messages shown above the save pill. */
	readonly errors: readonly string[];
	readonly id: string;
	readonly onDiscard: () => void;
	readonly onSave: () => void | Promise<void>;
	readonly saving: boolean;
}

interface EditSessionContextValue {
	readonly canSave: boolean;
	readonly dirty: boolean;
	readonly dirtyCount: number;
	readonly discardAll: () => void;
	readonly errors: readonly string[];
	readonly register: (section: EditSessionSection) => void;
	readonly saveAll: () => Promise<void>;
	readonly saving: boolean;
	readonly unregister: (id: string) => void;
}

const sameStringList = (a: readonly string[] | undefined, b: readonly string[] | undefined): boolean => {
	if (a === b) {
		return true;
	}
	if (!(a && b) || a.length !== b.length) {
		return false;
	}
	return a.every((item, index) => item === b[index]);
};

const EditSessionContext = createContext<EditSessionContextValue | null>(null);

const EMPTY_SECTION_ERRORS: readonly string[] = [];

export function EditSessionProvider({ children }: { readonly children: ReactNode }): JSX.Element {
	const [sections, setSections] = useState<Record<string, EditSessionSection>>({});
	const sectionsRef = useRef(sections);
	sectionsRef.current = sections;

	const register = useCallback((section: EditSessionSection) => {
		setSections((prev) => {
			const existing = prev[section.id];
			if (
				existing &&
				existing.dirty === section.dirty &&
				existing.canSave === section.canSave &&
				existing.saving === section.saving &&
				sameStringList(existing.errors, section.errors) &&
				existing.onSave === section.onSave &&
				existing.onDiscard === section.onDiscard
			) {
				return prev;
			}
			return { ...prev, [section.id]: section };
		});
	}, []);

	const unregister = useCallback((id: string) => {
		setSections((prev) => {
			if (!(id in prev)) {
				return prev;
			}
			const next = { ...prev };
			delete next[id];
			return next;
		});
	}, []);

	const list = useMemo(() => Object.values(sections), [sections]);
	const dirtySections = useMemo(() => list.filter((section) => section.dirty), [list]);
	const dirty = dirtySections.length > 0;
	const canSave =
		dirtySections.length > 0 && dirtySections.every((section) => section.canSave && section.errors.length === 0);
	const saving = list.some((section) => section.saving);
	const errors = useMemo(() => {
		const next: string[] = [];
		for (const section of dirtySections) {
			for (const message of section.errors) {
				if (!next.includes(message)) {
					next.push(message);
				}
			}
		}
		return next.length === 0 ? EMPTY_SECTION_ERRORS : next;
	}, [dirtySections]);

	const saveAll = useCallback(async () => {
		const current = Object.values(sectionsRef.current).filter((section) => section.dirty);
		if (current.some((section) => !section.canSave || section.errors.length > 0)) {
			return;
		}
		for (const section of current) {
			await section.onSave();
		}
	}, []);

	const discardAll = useCallback(() => {
		const current = Object.values(sectionsRef.current).filter((section) => section.dirty);
		for (const section of current) {
			section.onDiscard();
		}
	}, []);

	const value = useMemo(
		(): EditSessionContextValue => ({
			register,
			unregister,
			dirty,
			canSave,
			saving,
			dirtyCount: dirtySections.length,
			errors,
			saveAll,
			discardAll,
		}),
		[register, unregister, dirty, canSave, saving, dirtySections.length, errors, saveAll, discardAll]
	);

	return <EditSessionContext.Provider value={value}>{children}</EditSessionContext.Provider>;
}

export const useEditSession = (): EditSessionContextValue => {
	const ctx = useContext(EditSessionContext);
	if (!ctx) {
		throw new Error("useEditSession requires EditSessionProvider");
	}
	return ctx;
};

export const useEditSessionOptional = (): EditSessionContextValue | null => useContext(EditSessionContext);

interface UseEditSessionSectionInput {
	readonly canSave: boolean;
	readonly dirty: boolean;
	readonly enabled?: boolean;
	readonly errors?: readonly string[];
	readonly id: string;
	readonly onDiscard: () => void;
	readonly onSave: () => void | Promise<void>;
	readonly saving?: boolean;
}

/** Register a dirty form section with the page edit session (pill + leave guard). */
export const useEditSessionSection = ({
	id,
	dirty,
	canSave,
	saving = false,
	errors = EMPTY_SECTION_ERRORS,
	onSave,
	onDiscard,
	enabled = true,
}: UseEditSessionSectionInput): void => {
	const session = useEditSessionOptional();
	const sessionRef = useRef(session);
	sessionRef.current = session;

	const onSaveRef = useRef(onSave);
	const onDiscardRef = useRef(onDiscard);
	onSaveRef.current = onSave;
	onDiscardRef.current = onDiscard;

	const stableSave = useCallback(async () => {
		await onSaveRef.current();
	}, []);
	const stableDiscard = useCallback(() => {
		onDiscardRef.current();
	}, []);

	useEffect(() => {
		const current = sessionRef.current;
		if (!(current && enabled)) {
			return;
		}
		current.register({
			id,
			dirty,
			canSave,
			saving,
			errors,
			onSave: stableSave,
			onDiscard: stableDiscard,
		});
	}, [id, dirty, canSave, saving, errors, enabled, stableSave, stableDiscard]);

	useEffect(() => {
		const current = sessionRef.current;
		if (!(current && enabled)) {
			return;
		}
		return () => {
			sessionRef.current?.unregister(id);
		};
	}, [id, enabled]);
};
